import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getProjects, createProject, updateProject, deleteProject, getUsers, getCostCodes, getProjectCostSummary, getClients, createClient } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/errors';
import { formatSEK } from '../../lib/format-utils';
import { cn } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../components/ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../../components/ui/popover';
import {
    Command,
    CommandInput,
    CommandList,
    CommandEmpty,
    CommandGroup,
    CommandItem,
} from '../../components/ui/command';
import { toast } from 'sonner';
import {
    Plus, Pencil, Trash2, FolderKanban, DollarSign, ChevronDown, ChevronUp,
    ChevronsUpDown, Check, Loader2, X,
} from 'lucide-react';

const NO_COST_CODE = '__none__';

// The fields the inline "new customer" form offers (Task 21.2b). NAME IS THE
// ONLY REQUIRED ONE — everything else on a customer record (addresses, VAT
// treatment, payment terms, contact) is filled in on the Customers page before
// the first invoice. The point of this form is not to duplicate that page; it
// is to stop an admin setting up a project for a new customer from having to
// leave the form, create the customer elsewhere, and start over.
function emptyInlineClient(name = '') {
    return { name, org_number: '', invoice_email: '' };
}

export default function ProjectsPage() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [projects, setProjects] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [costCodes, setCostCodes] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingProject, setEditingProject] = useState(null);
    const [clients, setClients] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        active: true,
        billable: false,
        hourly_rate: 0,
        cost_code_id: '',
        // FK → clients.id (PRD 10.2). NULLABLE AND OPTIONAL BY DESIGN: an
        // internal project has no customer, and forcing one would make an
        // admin invent a customer to describe their own overhead.
        //
        // It is also carried in this form so an EDIT preserves it: PUT
        // /projects/{id} takes the whole document, so a payload without
        // `client_id` clears the project's customer silently.
        client_id: '',
        budget_sek: '',
        member_emails: [],
        activities: [],
    });
    const [emailInput, setEmailInput] = useState('');
    const [newActivity, setNewActivity] = useState({ name: '' });
    const [saving, setSaving] = useState(false);
    const [showInactive, setShowInactive] = useState(false);
    const [costSummary, setCostSummary] = useState({});

    // ── Client selector (Task 21.2b) ────────────────────────────────────────
    const [clientPickerOpen, setClientPickerOpen] = useState(false);
    const [clientQuery, setClientQuery] = useState('');
    // The inline "new customer" form lives in the project dialog rather than
    // inside the popover: a nested form inside a Command list fights the
    // search input for keystrokes, and a nested dialog fights it for focus.
    const [newClient, setNewClient] = useState(null);
    const [creatingClient, setCreatingClient] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        getProjectCostSummary()
            .then(res => setCostSummary(res.data?.costs ?? {}))
            .catch(() => setCostSummary({}));
    }, []);

    const fetchData = async () => {
        try {
            const [projectsRes, usersRes, costCodesRes, clientsRes] = await Promise.all([
                getProjects({ all: true }),
                getUsers().catch(() => ({ data: [] })),
                getCostCodes().catch(() => ({ data: [] })),
                // Only active customers are offered — a deactivated customer
                // is one you have stopped invoicing. A project that already
                // points at one keeps doing so; `client_name` on the response
                // is resolved server-side regardless of active state, so the
                // list still shows it.
                //
                // Swallowed like the other two: the customer list is an extra
                // field on one form, and losing it must not take the whole
                // Projects page down with it.
                getClients({ include_inactive: false }).catch(() => ({ data: [] })),
            ]);
            setProjects(projectsRes.data);
            setAllUsers(usersRes.data || []);
            setCostCodes(costCodesRes.data || []);
            setClients(clientsRes.data || []);
        } catch (error) {
            toast.error(t('projects.failed_load'));
        } finally {
            setLoading(false);
        }
    };

    const fetchProjects = async () => {
        try {
            const res = await getProjects({ all: true });
            setProjects(res.data);
        } catch (error) {
            toast.error(t('projects.failed_load'));
        }
    };

    // Everything the client selector holds, cleared whenever the dialog opens
    // so a half-typed customer from a previous edit cannot leak into the next.
    const resetClientPicker = () => {
        setClientPickerOpen(false);
        setClientQuery('');
        setNewClient(null);
    };

    const openCreateDialog = () => {
        setEditingProject(null);
        setFormData({
            name: '',
            active: true,
            billable: false,
            hourly_rate: 0,
            cost_code_id: '',
            client_id: '',
            budget_sek: '',
            member_emails: [],
            activities: [],
        });
        setEmailInput('');
        setNewActivity({ name: '' });
        resetClientPicker();
        setDialogOpen(true);
    };

    const openEditDialog = (project) => {
        setEditingProject(project);
        setFormData({
            name: project.name,
            active: project.active,
            billable: project.billable || false,
            hourly_rate: project.hourly_rate || 0,
            cost_code_id: project.cost_code_id || '',
            client_id: project.client_id || '',
            budget_sek: project.budget_sek ?? '',
            member_emails: project.member_emails || [],
            activities: project.activities || [],
        });
        setEmailInput('');
        setNewActivity({ name: '' });
        resetClientPicker();
        setDialogOpen(true);
    };

    const selectClient = (client) => {
        setFormData((prev) => ({ ...prev, client_id: client.id }));
        setClientPickerOpen(false);
        setClientQuery('');
        setNewClient(null);
    };

    /**
     * Create a customer without leaving the project form.
     *
     * Only `name` is required; the record is completed on the Customers page
     * before an invoice is issued against it. The created customer is pushed
     * into the local list and selected immediately rather than re-fetched —
     * the POST already returned the whole record.
     */
    const handleCreateClientInline = async () => {
        const name = (newClient?.name || '').trim();
        if (!name) {
            toast.error(t('projects.client_name_required'));
            return;
        }
        setCreatingClient(true);
        try {
            const payload = { name };
            // Sent only when filled: an empty string is not "no org number",
            // and `invoice_email: ""` fails the backend's email validator.
            if (newClient.org_number.trim()) payload.org_number = newClient.org_number.trim();
            if (newClient.invoice_email.trim()) payload.invoice_email = newClient.invoice_email.trim();

            const res = await createClient(payload);
            setClients((prev) => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
            selectClient(res.data);
            toast.success(t('projects.client_created', { name: res.data.name }));
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('projects.failed_create_client')));
        } finally {
            setCreatingClient(false);
        }
    };

    const addEmailFromInput = () => {
        const email = emailInput.trim().toLowerCase();
        if (email && !formData.member_emails.includes(email)) {
            setFormData(prev => ({ ...prev, member_emails: [...prev.member_emails, email] }));
        }
        setEmailInput('');
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            toast.error(t('projects.name_required'));
            return;
        }

        if (formData.billable && (!formData.hourly_rate || formData.hourly_rate <= 0)) {
            toast.error(t('projects.rate_required'));
            return;
        }

        setSaving(true);
        try {
            const payload = {
                ...formData,
                hourly_rate: formData.billable ? parseFloat(formData.hourly_rate) : 0,
                cost_code_id: formData.cost_code_id || null,
                // Explicitly null rather than "" when no customer is chosen —
                // an internal project genuinely has none, and the backend types
                // this as Optional[str].
                client_id: formData.client_id || null,
                budget_sek: formData.budget_sek !== '' ? parseFloat(formData.budget_sek) || null : null,
                member_emails: formData.member_emails,
                activities: formData.activities.map(act => ({
                    ...act,
                    id: act.id || crypto.randomUUID(),
                })),
            };

            if (editingProject) {
                await updateProject(editingProject.id, payload);
                toast.success(t('projects.project_updated'));
            } else {
                await createProject(payload);
                toast.success(t('projects.project_created'));
            }
            setDialogOpen(false);
            fetchProjects();
        } catch (error) {
            const detail = error.response?.data?.detail;
            if (detail === 'error.project.cost_code_not_found') toast.error(t('projects.cost_code_not_found'));
            // Everything else goes through the shared resolver, which turns the
            // backend's `error.*` keys into real sentences — including
            // `error.projects.client_not_found`, the refusal an unknown or
            // cross-tenant customer produces.
            else toast.error(getApiErrorMessage(error, t('projects.failed_save')));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (project) => {
        if (!window.confirm(t('projects.delete_confirm', { name: project.name }))) return;

        try {
            await deleteProject(project.id);
            toast.success(t('projects.project_deleted'));
            fetchProjects();
        } catch (error) {
            toast.error(t('projects.failed_delete'));
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-foreground border-t-transparent animate-spin"></div>
            </div>
        );
    }

    const billableProjects = projects.filter(p => p.billable);
    const nonBillableProjects = projects.filter(p => !p.billable);
    const activeProjects = projects.filter(p => p.active);
    const inactiveProjects = projects.filter(p => !p.active);

    // The name shown on the combobox trigger. `clients` holds only ACTIVE
    // customers, so a project already pointing at a deactivated one falls back
    // to the `client_name` the server resolved for it — the trigger must never
    // read as "no customer" for a project that has one.
    const selectedClientName = formData.client_id
        ? (clients.find(c => c.id === formData.client_id)?.name
            || (editingProject?.client_id === formData.client_id ? editingProject.client_name : null))
        : null;

    const BudgetCell = ({ project }) => {
        if (!project.budget_sek) {
            return <span className="text-muted-foreground text-xs">—</span>;
        }
        return (
            <div className="min-w-[120px]">
                <div className="flex justify-between text-xs text-muted-foreground mb-0.5">
                    <span>{project.budget_reported_sek?.toLocaleString() ?? '—'}</span>
                    <span>{project.budget_sek?.toLocaleString()} SEK</span>
                </div>
                <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
                    <div
                        className={`h-full rounded-full ${
                            (project.budget_percentage_reported ?? 0) >= 100 ? 'bg-destructive' :
                            (project.budget_percentage_reported ?? 0) >= 90 ? 'bg-amber-500' :
                            'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(project.budget_percentage_reported ?? 0, 100)}%` }}
                    />
                </div>
                {project.budget_supplier_invoices_sek > 0 && (
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                        {t('projects.budget_supplier_invoices_note', {
                            amount: `${project.budget_supplier_invoices_sek.toLocaleString()} SEK`,
                        })}
                    </p>
                )}
            </div>
        );
    };

    const MarginCell = ({ project }) => {
        const budgetSek = project.budget_sek;
        if (!budgetSek) {
            return <span className="text-muted-foreground text-xs">—</span>;
        }
        const employerCost = costSummary[project.id] ?? 0;
        const margin = budgetSek - employerCost;
        const marginPct = budgetSek !== 0 ? margin / budgetSek : 0;
        let colorClass = 'text-green-600';
        if (marginPct < -0.1) colorClass = 'text-destructive';
        else if (margin < 0) colorClass = 'text-amber-500';
        return (
            <div className="space-y-0.5">
                <div className={`font-mono text-xs font-semibold ${colorClass}`}>
                    {formatSEK(margin)}
                </div>
                <div className="text-xs text-muted-foreground">
                    {t('projects.col_employer_cost')}: {formatSEK(employerCost)}
                </div>
            </div>
        );
    };

    const NameCell = ({ project }) => (
        <td>
            <div className="font-medium">{project.name}</div>
            <div className="flex items-center gap-2 mt-0.5">
                {project.project_code && (
                    <span className="text-xs font-mono text-muted-foreground">{project.project_code}</span>
                )}
                {project.activities?.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                        {project.project_code && '·'} Activities ({project.activities.length})
                    </span>
                )}
            </div>
            {/* `client_name` is resolved server-side from `client_id` at query
                time and never stored on the project, so it follows a customer
                rename immediately. It is display-only — the form sends
                `client_id`, never this. */}
            {project.client_name && (
                <div className="text-xs text-muted-foreground mt-0.5" data-testid={`project-client-${project.id}`}>
                    {t('projects.client_for', { name: project.client_name })}
                </div>
            )}
        </td>
    );

    return (
        <div className="space-y-6" data-testid="projects-page">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                <div>
                    <h1 className="font-heading text-4xl font-bold tracking-tight">{t('projects.title')}</h1>
                    <p className="text-muted-foreground mt-1">{t('projects.subtitle')}</p>
                </div>
                <Button
                    className="uppercase tracking-wider"
                    onClick={openCreateDialog}
                    data-testid="add-project-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('projects.add_project')}
                </Button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-4">
                <Card className="border border-border shadow-none">
                    <CardContent className="p-4">
                        <p className="text-xs uppercase tracking-widest text-muted-foreground">{t('projects.total')}</p>
                        <p className="font-mono text-3xl font-bold mt-1">{projects.length}</p>
                    </CardContent>
                </Card>
                <Card className="border border-border shadow-none">
                    <CardContent className="p-4">
                        <p className="text-xs uppercase tracking-widest text-muted-foreground">{t('projects.billable')}</p>
                        <p className="font-mono text-3xl font-bold mt-1 text-green-600">{billableProjects.length}</p>
                    </CardContent>
                </Card>
                <Card className="border border-border shadow-none">
                    <CardContent className="p-4">
                        <p className="text-xs uppercase tracking-widest text-muted-foreground">{t('projects.internal')}</p>
                        <p className="font-mono text-3xl font-bold mt-1">{nonBillableProjects.length}</p>
                    </CardContent>
                </Card>
            </div>

            {/* Active Projects List */}
            <Card className="border border-border shadow-none">
                <CardContent className="p-0">
                    {activeProjects.length === 0 ? (
                        <div className="p-8 text-center text-muted-foreground">
                            <FolderKanban className="w-12 h-12 mx-auto mb-4 opacity-30" />
                            <p>{t('projects.no_projects')}</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>{t('projects.col_name')}</th>
                                    <th>{t('projects.col_billing')}</th>
                                    <th>{t('projects.col_rate')}</th>
                                    <th>{t('projects.col_budget')}</th>
                                    <th>{t('projects.col_margin')}</th>
                                    <th>{t('projects.col_status')}</th>
                                    <th>{t('projects.col_created')}</th>
                                    <th className="text-right">{t('projects.col_actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activeProjects.map((project) => (
                                    <tr key={project.id} data-testid={`project-row-${project.id}`}>
                                        <NameCell project={project} />
                                        <td>
                                            {project.billable ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs uppercase tracking-wider bg-green-100 text-green-800">
                                                    <DollarSign className="w-3 h-3" />
                                                    {t('projects.billable_label')}
                                                </span>
                                            ) : (
                                                <span className="inline-block px-2 py-1 text-xs uppercase tracking-wider bg-muted text-muted-foreground">
                                                    {t('projects.internal_label')}
                                                </span>
                                            )}
                                        </td>
                                        <td className="font-mono">
                                            {project.billable ? `${project.hourly_rate?.toLocaleString()} SEK` : '-'}
                                        </td>
                                        <td>
                                            <BudgetCell project={project} />
                                        </td>
                                        <td>
                                            <MarginCell project={project} />
                                        </td>
                                        <td>
                                            <span className={`inline-block px-2 py-1 text-xs uppercase tracking-wider ${
                                                project.active
                                                    ? 'bg-foreground text-background'
                                                    : 'bg-muted text-muted-foreground'
                                            }`}>
                                                {project.active ? t('projects.active') : t('projects.inactive')}
                                            </span>
                                        </td>
                                        <td className="font-mono text-sm">
                                            {project.created_at ? new Date(project.created_at).toLocaleDateString() : "—"}
                                        </td>
                                        <td className="text-right">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => openEditDialog(project)}
                                                data-testid={`edit-project-${project.id}`}
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Inactive Projects Accordion */}
            {inactiveProjects.length > 0 && (
                <Card className="border border-border shadow-none">
                    <button
                        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/30 transition-colors"
                        onClick={() => setShowInactive(!showInactive)}
                    >
                        <span className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
                            {t('projects.inactive_projects', { count: inactiveProjects.length })}
                        </span>
                        {showInactive
                            ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
                            : <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        }
                    </button>
                    {showInactive && (
                        <CardContent className="p-0 border-t border-border">
                            <div className="overflow-x-auto">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>{t('projects.col_name')}</th>
                                            <th>{t('projects.col_billing')}</th>
                                            <th>{t('projects.col_rate')}</th>
                                            <th>{t('projects.col_budget')}</th>
                                            <th>{t('projects.col_margin')}</th>
                                            <th>{t('projects.col_status')}</th>
                                            <th>{t('projects.col_created')}</th>
                                            <th className="text-right">{t('projects.col_actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="opacity-60">
                                        {inactiveProjects.map((project) => (
                                            <tr key={project.id} data-testid={`project-row-${project.id}`}>
                                                <NameCell project={project} />
                                                <td>
                                                    {project.billable ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs uppercase tracking-wider bg-green-100 text-green-800">
                                                            <DollarSign className="w-3 h-3" />
                                                            {t('projects.billable_label')}
                                                        </span>
                                                    ) : (
                                                        <span className="inline-block px-2 py-1 text-xs uppercase tracking-wider bg-muted text-muted-foreground">
                                                            {t('projects.internal_label')}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="font-mono">
                                                    {project.billable ? `${project.hourly_rate?.toLocaleString()} SEK` : '-'}
                                                </td>
                                                <td>
                                                    <BudgetCell project={project} />
                                                </td>
                                                <td>
                                                    <MarginCell project={project} />
                                                </td>
                                                <td>
                                                    <span className="inline-block px-2 py-1 text-xs uppercase tracking-wider bg-muted text-muted-foreground">
                                                        {t('projects.inactive')}
                                                    </span>
                                                </td>
                                                <td className="font-mono text-sm">
                                                    {project.created_at ? new Date(project.created_at).toLocaleDateString() : "—"}
                                                </td>
                                                <td className="text-right">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => openEditDialog(project)}
                                                        data-testid={`edit-project-${project.id}`}
                                                    >
                                                        <Pencil className="w-4 h-4" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    )}
                </Card>
            )}

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">
                            {editingProject ? t('projects.edit_project') : t('projects.new_project')}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        {/* Cost Code — first, anchors the project identity */}
                        <div>
                            <Label className="label-uppercase mb-2 block">{t('projects.label_cost_code')}</Label>
                            <Select
                                value={formData.cost_code_id || NO_COST_CODE}
                                onValueChange={(v) => setFormData({ ...formData, cost_code_id: v === NO_COST_CODE ? '' : v })}
                            >
                                <SelectTrigger data-testid="project-cost-code-select">
                                    <SelectValue placeholder={t('projects.cost_code_placeholder')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={NO_COST_CODE}>{t('projects.cost_code_none')}</SelectItem>
                                    {costCodes
                                        .filter(cc => cc.active || cc.id === formData.cost_code_id)
                                        .map(cc => (
                                            <SelectItem key={cc.id} value={cc.id}>
                                                <span className="font-mono font-semibold">{cc.code}</span>
                                                {' — '}
                                                {cc.name}
                                                {!cc.active && (
                                                    <span className="text-muted-foreground"> ({t('common.inactive')})</span>
                                                )}
                                            </SelectItem>
                                        ))}
                                </SelectContent>
                            </Select>
                            {costCodes.length === 0 && (
                                <p className="text-xs text-muted-foreground mt-1">{t('projects.cost_code_hint_empty')}</p>
                            )}
                        </div>

                        {/* Customer (Task 21.2b, PRD 10.2).
                            OPTIONAL, and stays optional — an internal project
                            has no customer. Chosen from the existing customers
                            rather than typed free-text, because `client_id` is
                            what links this project's billable work to an
                            invoice; a name typed by hand links to nothing. */}
                        <div>
                            <Label htmlFor="project-client-trigger" className="label-uppercase mb-2 block">
                                {t('projects.label_client')}
                            </Label>
                            <div className="flex gap-2">
                                <Popover open={clientPickerOpen} onOpenChange={setClientPickerOpen}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            id="project-client-trigger"
                                            type="button"
                                            variant="outline"
                                            role="combobox"
                                            aria-expanded={clientPickerOpen}
                                            className="flex-1 justify-between font-normal"
                                            data-testid="project-client-trigger"
                                        >
                                            <span className="truncate">
                                                {formData.client_id
                                                    ? (selectedClientName || t('projects.client_unknown'))
                                                    : t('projects.client_placeholder')}
                                            </span>
                                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[--radix-popper-anchor-width] p-0" align="start">
                                        <Command>
                                            <CommandInput
                                                placeholder={t('projects.client_search_placeholder')}
                                                value={clientQuery}
                                                onValueChange={setClientQuery}
                                            />
                                            <CommandList>
                                                <CommandEmpty>
                                                    {clientQuery.trim() ? (
                                                        <button
                                                            type="button"
                                                            className="w-full text-left px-3 py-2 text-sm hover:bg-accent flex items-center gap-2"
                                                            onClick={() => {
                                                                // Hands the typed text to the inline form
                                                                // rather than creating on the spot: a
                                                                // customer is worth one screen of detail,
                                                                // and the org number is usually to hand.
                                                                setNewClient(emptyInlineClient(clientQuery.trim()));
                                                                setClientPickerOpen(false);
                                                            }}
                                                            data-testid="project-add-client-inline"
                                                        >
                                                            <Plus className="w-4 h-4" />
                                                            {t('projects.add_new_client', { name: clientQuery.trim() })}
                                                        </button>
                                                    ) : (
                                                        <span className="text-muted-foreground text-sm">
                                                            {t('projects.no_clients_found')}
                                                        </span>
                                                    )}
                                                </CommandEmpty>
                                                <CommandGroup>
                                                    {/* Clearing the customer is a real choice, not an
                                                        absence of one — most projects are internal. */}
                                                    <CommandItem
                                                        value={t('projects.client_none')}
                                                        onSelect={() => selectClient({ id: '' })}
                                                        data-testid="project-client-none"
                                                    >
                                                        <Check
                                                            className={cn(
                                                                'mr-2 h-4 w-4',
                                                                formData.client_id ? 'opacity-0' : 'opacity-100'
                                                            )}
                                                        />
                                                        {t('projects.client_none')}
                                                    </CommandItem>
                                                    {/* cmdk keys an item by its `value`, so two
                                                        customers sharing a name — real, and more
                                                        likely with "[DEMO SEED]" records around —
                                                        would collide and selection could land on
                                                        the wrong one. The id makes the value
                                                        unique; `keywords` keeps the name (and only
                                                        the name) searchable. */}
                                                    {clients.map((client) => (
                                                        <CommandItem
                                                            key={client.id}
                                                            value={`${client.name} ${client.id}`}
                                                            keywords={[client.name]}
                                                            onSelect={() => selectClient(client)}
                                                            data-testid={`client-option-${client.id}`}
                                                        >
                                                            <Check
                                                                className={cn(
                                                                    'mr-2 h-4 w-4',
                                                                    formData.client_id === client.id
                                                                        ? 'opacity-100'
                                                                        : 'opacity-0'
                                                                )}
                                                            />
                                                            {client.name}
                                                        </CommandItem>
                                                    ))}
                                                </CommandGroup>
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setNewClient(emptyInlineClient())}
                                    data-testid="project-new-client-btn"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    {t('projects.new_client')}
                                </Button>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                                {t('projects.client_hint')}
                            </p>

                            {/* Inline "new customer" — name only is required; the
                                rest of the record is completed on the Customers
                                page before an invoice is issued. */}
                            {newClient && (
                                <div className="border border-border p-3 mt-3 space-y-3" data-testid="inline-client-form">
                                    <div className="flex items-start justify-between gap-2">
                                        <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
                                            {t('projects.new_client_title')}
                                        </p>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 w-6 p-0"
                                            onClick={() => setNewClient(null)}
                                            aria-label={t('common.cancel')}
                                        >
                                            <X className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    <div>
                                        <Label htmlFor="new-client-name" className="label-uppercase mb-2 block text-xs">
                                            {t('projects.new_client_name')}
                                        </Label>
                                        <Input
                                            id="new-client-name"
                                            value={newClient.name}
                                            onChange={(e) => setNewClient((prev) => ({ ...prev, name: e.target.value }))}
                                            placeholder={t('projects.new_client_name_placeholder')}
                                            data-testid="new-client-name-input"
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                            <Label htmlFor="new-client-org" className="label-uppercase mb-2 block text-xs">
                                                {t('projects.new_client_org_number')}
                                            </Label>
                                            <Input
                                                id="new-client-org"
                                                value={newClient.org_number}
                                                onChange={(e) => setNewClient((prev) => ({ ...prev, org_number: e.target.value }))}
                                                data-testid="new-client-org-input"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="new-client-email" className="label-uppercase mb-2 block text-xs">
                                                {t('projects.new_client_invoice_email')}
                                            </Label>
                                            <Input
                                                id="new-client-email"
                                                type="email"
                                                value={newClient.invoice_email}
                                                onChange={(e) => setNewClient((prev) => ({ ...prev, invoice_email: e.target.value }))}
                                                data-testid="new-client-email-input"
                                            />
                                        </div>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        {t('projects.new_client_rest_hint')}
                                    </p>
                                    <Button
                                        type="button"
                                        size="sm"
                                        onClick={handleCreateClientInline}
                                        disabled={creatingClient || !newClient.name.trim()}
                                        data-testid="create-client-btn"
                                    >
                                        {creatingClient && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                        {t('projects.create_client_cta')}
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Name */}
                        <div>
                            <Label className="label-uppercase mb-2 block">{t('projects.label_name')}</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder={t('projects.placeholder_name')}
                                data-testid="project-name-input"
                            />
                        </div>

                        {/* Active toggle */}
                        <div className="flex items-center justify-between">
                            <Label className="label-uppercase">{t('projects.label_active')}</Label>
                            <Switch
                                checked={formData.active}
                                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                                data-testid="project-active-switch"
                            />
                        </div>

                        {/* Billable + rate */}
                        <div className="border-t border-border pt-4">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <Label className="label-uppercase">{t('projects.label_billable')}</Label>
                                    <p className="text-xs text-muted-foreground mt-1">{t('projects.billable_hint')}</p>
                                </div>
                                <Switch
                                    checked={formData.billable}
                                    onCheckedChange={(checked) => setFormData({
                                        ...formData,
                                        billable: checked,
                                        hourly_rate: checked ? formData.hourly_rate || 800 : 0
                                    })}
                                    data-testid="project-billable-switch"
                                />
                            </div>

                            {formData.billable && (
                                <div>
                                    <Label className="label-uppercase mb-2 block">{t('projects.label_hourly_rate')}</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="50"
                                        value={formData.hourly_rate}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            hourly_rate: parseFloat(e.target.value) || 0
                                        })}
                                        placeholder={t('projects.placeholder_rate')}
                                        data-testid="project-hourly-rate-input"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {t('projects.hourly_rate_hint')}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Budget */}
                        <div className="border-t border-border pt-4">
                            <Label className="label-uppercase mb-2 block">{t('projects.label_budget')}</Label>
                            <Input
                                type="number"
                                min="0"
                                step="1000"
                                value={formData.budget_sek}
                                onChange={(e) => setFormData({ ...formData, budget_sek: e.target.value })}
                                placeholder={t('projects.budget_placeholder')}
                            />
                            {editingProject && editingProject.budget_sek && (
                                <div className="mt-3 space-y-1">
                                    <div className="flex justify-between text-xs text-muted-foreground">
                                        <span>{t('projects.budget_approved')}: {editingProject.budget_approved_sek?.toLocaleString() ?? '—'} SEK</span>
                                        <span>{t('projects.budget_reported')}: {editingProject.budget_reported_sek?.toLocaleString() ?? '—'} SEK</span>
                                        <span>{t('projects.budget_total')}: {editingProject.budget_sek?.toLocaleString()} SEK</span>
                                    </div>
                                    <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                                        <div
                                            className={`h-full rounded-full transition-all ${
                                                (editingProject.budget_percentage_reported ?? 0) >= 100 ? 'bg-destructive' :
                                                (editingProject.budget_percentage_reported ?? 0) >= 90 ? 'bg-amber-500' :
                                                'bg-green-500'
                                            }`}
                                            style={{ width: `${Math.min(editingProject.budget_percentage_reported ?? 0, 100)}%` }}
                                        />
                                    </div>
                                    <p className="text-xs text-muted-foreground text-right">
                                        {(editingProject.budget_percentage_reported ?? 0).toFixed(1)}% {t('projects.budget_reported_pct')}
                                    </p>
                                    {editingProject.budget_supplier_invoices_sek > 0 && (
                                        <p className="text-xs text-muted-foreground text-right">
                                            {t('projects.budget_supplier_invoices_note', {
                                                amount: `${editingProject.budget_supplier_invoices_sek.toLocaleString()} SEK`,
                                            })}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Members */}
                        <div className="border-t border-border pt-4">
                            <Label className="label-uppercase mb-2 block">{t('projects.label_members')}</Label>
                            <p className="text-xs text-muted-foreground mb-2">{t('projects.members_hint')}</p>

                            {formData.member_emails.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-2">
                                    {formData.member_emails.map((email) => {
                                        const userExists = allUsers.some(
                                            u => u.email.toLowerCase() === email.toLowerCase() && u.is_active !== false
                                        );
                                        return (
                                            <span
                                                key={email}
                                                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${
                                                    userExists
                                                        ? 'bg-muted'
                                                        : 'bg-amber-100 text-amber-800 border border-amber-300'
                                                }`}
                                            >
                                                {email}
                                                {!userExists && (
                                                    <span title={t('projects.member_no_match')} className="ml-0.5">
                                                        &#9888;
                                                    </span>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => setFormData(prev => ({
                                                        ...prev,
                                                        member_emails: prev.member_emails.filter(e => e !== email)
                                                    }))}
                                                    className="ml-1 hover:text-destructive"
                                                    aria-label={`Remove ${email}`}
                                                >
                                                    &times;
                                                </button>
                                            </span>
                                        );
                                    })}
                                </div>
                            )}

                            <div className="flex gap-2">
                                <Input
                                    type="email"
                                    value={emailInput}
                                    onChange={(e) => setEmailInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ',') {
                                            e.preventDefault();
                                            addEmailFromInput();
                                        }
                                    }}
                                    placeholder={t('projects.member_email_placeholder')}
                                />
                                <Button type="button" variant="outline" size="sm" onClick={addEmailFromInput}>
                                    {t('common.add')}
                                </Button>
                            </div>
                        </div>

                        {/* Activities */}
                        <div className="border-t border-border pt-4">
                            <Label className="label-uppercase mb-3 block">{t('projects.label_activities')}</Label>

                            {formData.activities.length > 0 && (
                                <div className="space-y-1 mb-3">
                                    {formData.activities.map((act, idx) => (
                                        <div key={act.id} className="flex gap-2 items-center">
                                            <Input
                                                value={act.name}
                                                onChange={(e) => setFormData(prev => ({
                                                    ...prev,
                                                    activities: prev.activities.map((a, i) =>
                                                        i === idx ? { ...a, name: e.target.value } : a
                                                    )
                                                }))}
                                                placeholder={t('projects.activity_name_placeholder')}
                                                className="flex-1 text-sm h-8"
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="text-destructive hover:text-destructive h-8 w-8 p-0"
                                                onClick={() => setFormData(prev => ({
                                                    ...prev,
                                                    activities: prev.activities.filter((_, i) => i !== idx)
                                                }))}
                                                aria-label={`Remove activity ${act.name}`}
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="flex gap-2 items-center">
                                <Input
                                    value={newActivity.name}
                                    onChange={(e) => setNewActivity(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder={t('projects.activity_name_placeholder')}
                                    className="flex-1 text-sm h-8"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') e.preventDefault();
                                    }}
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-8"
                                    onClick={() => {
                                        if (!newActivity.name.trim()) return;
                                        setFormData(prev => ({
                                            ...prev,
                                            activities: [...prev.activities, {
                                                id: crypto.randomUUID(),
                                                name: newActivity.name.trim(),
                                            }]
                                        }));
                                        setNewActivity({ name: '' });
                                    }}
                                >
                                    <Plus className="w-3 h-3 mr-1" />
                                    {t('common.add')}
                                </Button>
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="flex-col sm:flex-row gap-2">
                        {editingProject && !formData.active && (
                            <Button
                                variant="destructive"
                                onClick={() => handleDelete(editingProject)}
                                className="sm:mr-auto"
                                data-testid={`delete-project-${editingProject?.id}`}
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                {t('projects.delete_project')}
                            </Button>
                        )}
                        {editingProject && formData.active && (
                            <p className="text-xs text-muted-foreground sm:mr-auto self-center">
                                {t('projects.delete_hint')}
                            </p>
                        )}
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button onClick={handleSave} disabled={saving} data-testid="save-project-btn">
                            {saving ? t('common.saving') : t('common.save')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
